// Module 9 (companion) — the compliance check register.
// Route: /reinsurance/compliance
//
// One row per act of checking. The counterparty record carries the current
// position; this carries the evidence and the history, which is what a
// regulator actually asks for.

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import {
  C, Icons, Kpi, Pill, Panel, Row, MiniTable, TwoLine,
  PrimaryButton, pct, date, humanise,
} from './kit';

const resultTone = (r) => (r === 'pass' ? 'clear' : r === 'fail' ? 'flagged' : r === 'waived' ? 'draft' : 'under_review');

export default function ComplianceChecks() {
  const location = useLocation();
  const { t } = useTranslation();
  const counterparty = new URLSearchParams(location.search).get('counterparty');

  const columns = [
    { key: 'checkNumber', label: t('screens.reCompliance.colNumber', 'Check'), width: 168,
      render: (r) => <TwoLine primary={r.checkNumber} secondary={humanise(r.type)} /> },
    { key: 'subject', label: t('screens.reCompliance.colSubject', 'Subject'),
      render: (r) => r.counterpartyName || r.treatyNumber || <span style={{ color: C.faint }}>unattributed</span> },
    { key: 'jurisdiction', label: t('screens.reCompliance.colJurisdiction', 'Jurisdiction'), width: 116,
      render: (r) => r.jurisdiction || '—' },
    { key: 'result', label: t('screens.reCompliance.colResult', 'Result'), width: 110,
      render: (r) => <Pill status={resultTone(r.result)} label={humanise(r.result)} /> },
    { key: 'evidence', label: t('screens.reCompliance.colEvidence', 'Evidence'),
      // The two jurisdiction-specific shapes carry their own completeness
      // rule, computed on write, so the register can answer "is the file
      // complete" without opening every row.
      render: (r) => {
        if (r.type === 'local_market_first') {
          const n = r.localMarketFirst?.marketsApproached?.length || 0;
          return (
            <span>
              <Pill status={r.localMarketFirst?.evidenceComplete ? 'complete' : 'under_review'}
                label={r.localMarketFirst?.evidenceComplete ? 'Complete' : 'Incomplete'} size='sm' />
              <span style={{ display: 'block', fontSize: 11.5, color: C.muted, marginTop: 3 }}>
                {n} market{n === 1 ? '' : 's'} approached
                {r.localMarketFirst?.localSharePercent != null ? ` · ${pct(r.localMarketFirst.localSharePercent)} local` : ''}
              </span>
            </span>
          );
        }
        if (r.type === 'commission_disclosure') {
          return <Pill status={r.commissionDisclosure?.complete ? 'complete' : 'under_review'}
            label={r.commissionDisclosure?.complete ? 'Disclosed' : 'Pending'} size='sm' />;
        }
        const docs = (r.documents || []).length;
        return docs ? `${docs} document${docs === 1 ? '' : 's'}` : <span style={{ color: C.faint }}>—</span>;
      } },
    { key: 'performedAt', label: t('screens.reCompliance.colPerformed', 'Performed'), width: 112,
      render: (r) => date(r.performedAt) },
    { key: 'expiresAt', label: t('screens.reCompliance.colExpires', 'Expires'), width: 112,
      render: (r) => {
        if (!r.expiresAt) return <span style={{ color: C.faint }}>—</span>;
        const gone = new Date(r.expiresAt) < new Date();
        return <span style={{ color: gone ? C.red : C.body, fontWeight: gone ? 700 : 400 }}>{date(r.expiresAt)}</span>;
      } },
  ];

  return (
    <ModulePage
      title={t('screens.reCompliance.title', 'Compliance checks')}
      subtitle={t('screens.reCompliance.subtitle', 'The evidence behind every compliance position')}
      endpoint='/reinsurance/compliance-checks'
      searchPlaceholder={t('screens.reCompliance.search', 'Search checks, counterparties, jurisdiction…')}
      initialQuery={counterparty ? { counterparty } : {}}
      actions={<PrimaryButton>{t('screens.reCompliance.new', 'Record check')}</PrimaryButton>}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.totalChecks?.value ?? '—'} trend={s?.totalChecks?.trend}
            comparison={s?.totalChecks?.comparison}
            label={t('screens.reCompliance.kpiTotal', 'Checks on file')} />
          <Kpi icon={Icons.check()} tone='green'
            value={s?.passed?.value ?? '—'} trend={s?.passed?.trend}
            comparison={s?.passed?.comparison}
            label={t('screens.reCompliance.kpiPassed', 'Cleared')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.failed?.value ?? '—'} trend={s?.failed?.trend}
            comparison={s?.failed?.comparison}
            label={t('screens.reCompliance.kpiFailed', 'Require action')} />
          <Kpi icon={Icons.clock()} tone='amber'
            value={s?.expiringSoon?.value ?? '—'} trend={s?.expiringSoon?.trend}
            comparison={s?.expiringSoon?.comparison}
            label={t('screens.reCompliance.kpiExpiring', 'Expiring soon')} />
        </>
      )}
      filters={[
        { key: 'type', label: 'Type', options: [
          { value: 'all', label: 'All check types' },
          { value: 'kyc', label: 'KYC' },
          { value: 'sanctions', label: 'Sanctions' },
          { value: 'credit', label: 'Credit' },
          { value: 'local_market_first', label: 'Local market first' },
          { value: 'commission_disclosure', label: 'Commission disclosure' },
          { value: 'conduct', label: 'Conduct' },
        ] },
        { key: 'result', label: 'Result', options: [
          { value: 'all', label: 'Any result' },
          { value: 'pass', label: 'Pass' },
          { value: 'fail', label: 'Fail' },
          { value: 'review', label: 'Review' },
          { value: 'pending', label: 'Pending' },
          { value: 'waived', label: 'Waived' },
        ] },
        { key: 'jurisdiction', label: 'Jurisdiction', options: [
          { value: 'all', label: 'All jurisdictions' },
          { value: 'SA', label: 'Saudi Arabia' },
          { value: 'AE', label: 'United Arab Emirates' },
          { value: 'BH', label: 'Bahrain' },
          { value: 'QA', label: 'Qatar' },
          { value: 'KW', label: 'Kuwait' },
          { value: 'OM', label: 'Oman' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reCompliance.empty', 'No checks recorded yet.')}
      drawerTabs={['Overview', 'Evidence']}
      drawerHeader={(r) => ({
        title: r.checkNumber,
        badge: <Pill status={resultTone(r.result)} label={humanise(r.result)} />,
        lines: [r.counterpartyName || r.treatyNumber, [humanise(r.type), r.jurisdiction].filter(Boolean).join(' · ')].filter(Boolean),
      })}
      drawer={(r, { tab }) => {
        if (tab === 'Evidence' && r.type === 'local_market_first') {
          return (
            <Panel title='Markets approached'>
              <MiniTable
                head={['Market', 'Approached', 'Response', 'Share']}
                rows={(r.localMarketFirst?.marketsApproached || []).map(m => [
                  m.name || '—', date(m.approachedAt), humanise(m.response), pct(m.sharePercent, 0),
                ])}
                empty='No markets recorded.'
              />
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10 }}>
                Evidence is complete only when every market has a response recorded
                and a local share is stated.
              </div>
            </Panel>
          );
        }
        if (tab === 'Evidence') {
          return (
            <Panel title='Documents'>
              {(r.documents || []).length === 0
                ? <div style={{ fontSize: 12.5, color: C.faint }}>No documents attached.</div>
                : (r.documents || []).map((d, i) => <Row key={i} label={d.label || d.filename} value={date(d.uploadedAt)} />)}
            </Panel>
          );
        }
        return (
          <>
            <Panel title='Check information'>
              <Row label='Check no.' value={r.checkNumber} />
              <Row label='Type' value={humanise(r.type)} />
              <Row label='Subject' value={r.counterpartyName || r.treatyNumber} />
              <Row label='Jurisdiction' value={r.jurisdiction} />
              <Row label='Result' value={<Pill status={resultTone(r.result)} label={humanise(r.result)} size='sm' />} />
              <Row label='Performed' value={date(r.performedAt)} />
              <Row label='Expires' value={date(r.expiresAt)} />
            </Panel>
            {r.type === 'local_market_first' && (
              <Panel title='Local market first'>
                <Row label='Offered' value={date(r.localMarketFirst?.offeredAt)} />
                <Row label='Markets approached' value={(r.localMarketFirst?.marketsApproached || []).length} />
                <Row label='Local share' value={pct(r.localMarketFirst?.localSharePercent)} />
                <Row label='Evidence complete' value={r.localMarketFirst?.evidenceComplete ? 'Yes' : 'No'} />
              </Panel>
            )}
            {r.type === 'commission_disclosure' && (
              <Panel title='Commission disclosure'>
                <Row label='Disclosed' value={date(r.commissionDisclosure?.disclosedAt)} />
                <Row label='Disclosed to' value={r.commissionDisclosure?.disclosedTo} />
                <Row label='Method' value={humanise(r.commissionDisclosure?.method)} />
                <Row label='Brokerage' value={pct(r.commissionDisclosure?.brokeragePercent, 2)} />
              </Panel>
            )}
            {r.findings && (
              <Panel title='Findings'>
                <div style={{ fontSize: 12.5, color: C.body, lineHeight: 1.6 }}>{r.findings}</div>
              </Panel>
            )}
          </>
        );
      }}
    />
  );
}
